import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaymentService } from '../payment.service';
import { PaymentSource } from '../payment.type';
import { AuthService } from '../../auth/auth.service';

interface SourceForm {
  id?: string;
  name: string;
  kind: 'Bank' | 'Wallet';
  hesabfa_code: string;
  currency: string;
  is_unspecified: boolean;
}

/**
 * The Payments "Paid-from accounts" tab: our own bank accounts and wallets a
 * payout leaves from. The admin who pays picks one, so the Hesabfa export can
 * record where the money came from; each carries its code in Hesabfa (the
 * bank's code, or the wallet's cash-box code). One that was paid from can only
 * be deactivated, not deleted.
 */
@Component({
  selector: 'app-payment-sources',
  imports: [
    CommonModule, FormsModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatProgressSpinnerModule, MatSelectModule, MatSlideToggleModule, MatTableModule, MatTooltipModule,
  ],
  templateUrl: './payment-sources.html',
  styleUrl: './payment-sources.scss',
})
export class PaymentSources implements OnInit {
  private paymentService = inject(PaymentService);
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  private snackBar = inject(MatSnackBar);

  readonly columns = ['name', 'kind', 'code', 'currency', 'active', 'actions'];

  sources: PaymentSource[] = [];
  loading = false;
  error: string | null = null;
  /** The add/edit form, when open. */
  form: SourceForm | null = null;
  saving = false;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getPaymentSources().subscribe({
      next: s => { this.sources = s; this.loading = false; },
      error: err => { this.error = err?.error?.error ?? 'Failed to load the accounts'; this.loading = false; },
    });
  }

  add(): void {
    this.form = { name: '', kind: 'Bank', hesabfa_code: '', currency: '', is_unspecified: false };
  }

  edit(s: PaymentSource): void {
    this.form = { id: s.id, name: s.name, kind: s.kind, hesabfa_code: s.hesabfa_code, currency: s.currency, is_unspecified: s.is_unspecified };
  }

  save(): void {
    const f = this.form;
    if (!f || this.saving || !f.name.trim() || !f.hesabfa_code.trim()) return;
    this.saving = true;
    const done = {
      next: (saved: PaymentSource) => {
        this.saving = false;
        this.form = null;
        this.snackBar.open(`${saved.name} saved`, undefined, { duration: 2500 });
        this.load();
      },
      error: (err: any) => { this.saving = false; this.fail(err); },
    };
    if (f.id) {
      this.paymentService.updatePaymentSource(f.id, {
        name: f.name.trim(), hesabfa_code: f.hesabfa_code.trim(), currency: f.currency.trim() || undefined,
        is_unspecified: f.kind === 'Bank' && f.is_unspecified,
      }).subscribe(done);
    } else {
      this.paymentService.createPaymentSource({
        name: f.name.trim(), kind: f.kind, hesabfa_code: f.hesabfa_code.trim(), currency: f.currency.trim() || undefined,
        is_unspecified: f.kind === 'Bank' && f.is_unspecified,
      }).subscribe(done);
    }
  }

  setActive(s: PaymentSource, active: boolean): void {
    this.paymentService.updatePaymentSource(s.id, { active }).subscribe({
      next: updated => { this.sources = this.sources.map(x => x.id === updated.id ? updated : x); },
      error: err => { this.fail(err); this.load(); },
    });
  }

  remove(s: PaymentSource): void {
    if (!confirm(`Delete ${s.name}? This only works while nothing was paid from it.`)) return;
    this.paymentService.deletePaymentSource(s.id).subscribe({
      next: () => { this.sources = this.sources.filter(x => x.id !== s.id); },
      error: err => this.fail(err),
    });
  }

  private fail(err: any): void {
    this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 8000 });
  }
}
